import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { mfs100Verify, SCANNER_STATE } from "@/lib/mantra";
import { useCaptureFlow } from "./CaptureModal";
import { StatePill } from "./ScannerStatus";

export default function VerifyModal({ open, onClose, candidate }) {
  const fingers = Object.keys(candidate?.fingers || {});
  const [finger, setFinger] = useState(fingers[0] || "");
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState(false);
  const flow = useCaptureFlow();

  useEffect(() => { if (open) { setFinger(Object.keys(candidate?.fingers || {})[0] || ""); setOutcome(null); flow.reset(); } }, [open, candidate]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setOutcome(null);
    const fp = candidate.fingers[finger];
    if (!fp) return;
    if (!fp.has_template) {
      toast.error("This finger was captured via RD Service only — no template available for local matching.");
      return;
    }
    const cap = await flow.capture();
    const live = flow.status;
    if (!cap) return;
    if (cap.mode !== "mfs100_client") {
      toast.error("Local matching requires the Mantra Client Service (template mode). RD Service returns encrypted PID only — matching not available.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/attendance/fingerprints/${fp.id}/template`);
      const r = await mfs100Verify(live?.client?.base || cap.base, cap.template, data.template);
      if (String(r.ErrorCode) !== "0") throw new Error(r.ErrorDescription || "Verification failed on device service");
      const matched = r.Status === true || r.Status === "true";
      const res = await api.post(`/attendance/fingerprints/${fp.id}/verify-result`, { matched, quality_score: cap.quality, device: cap.device });
      setOutcome(res.data);
    } catch (e) {
      toast.error(e.response ? formatApiError(e.response?.data?.detail) : e.message);
    } finally { setBusy(false); }
  };

  if (!candidate) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="verify-modal" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Verify Fingerprint</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-xs text-slate-500">Candidate</p><p className="font-semibold">{candidate.name}</p></div>
          <div><p className="text-xs text-slate-500">Candidate ID</p><p className="font-semibold">{candidate.candidate_code}</p></div>
        </div>
        {fingers.length === 0 ? <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">No fingerprints captured for this candidate yet.</p> : (
          <div>
            <p className="text-xs text-slate-500 mb-1">Select Finger</p>
            <Select value={finger} onValueChange={(v) => { setFinger(v); setOutcome(null); flow.reset(); }}>
              <SelectTrigger data-testid="verify-finger-select"><SelectValue /></SelectTrigger>
              <SelectContent>{fingers.map((n) => <SelectItem key={n} value={n}>Finger {n}{candidate.fingers[n].has_template ? "" : " (no template — not matchable)"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
          <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Status</span><StatePill state={flow.phase === SCANNER_STATE.READY ? flow.status.state : flow.phase} testid="verify-state" /></div>
          {flow.phase === SCANNER_STATE.CAPTURING && (<><p className="text-sm text-emerald-800 font-medium text-center">Place your finger on the Mantra scanner</p><Progress value={flow.progress} className="h-2" /></>)}
          {flow.error && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="verify-error">{flow.error}</p>}
          {outcome && (
            <div data-testid="verify-result" className={`rounded-xl p-4 text-center ${outcome.result === "MATCH" ? "bg-emerald-100" : "bg-red-100"}`}>
              <p className={`font-display text-2xl font-bold ${outcome.result === "MATCH" ? "text-emerald-800" : "text-red-700"}`}>{outcome.result}</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-slate-700 mt-3 text-left">
                <span>Candidate: <b>{outcome.candidate?.name}</b></span><span>Finger: <b>{outcome.finger_position}</b></span>
                <span>Quality: <b>{outcome.quality_score ?? "—"}%</b></span><span>Verified By: <b>{outcome.verified_by}</b></span>
                <span className="col-span-2">Date/Time: <b>{new Date(outcome.verified_at).toLocaleString()}</b></span>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" data-testid="verify-close" onClick={onClose} className="rounded-xl">Close</Button>
          <Button data-testid="verify-start" onClick={run} disabled={!finger || busy || flow.phase === SCANNER_STATE.CAPTURING} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white">{busy ? "Matching…" : outcome ? "Verify Again" : "Capture & Verify"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
